import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { DashboardLayout } from '../components/DashboardLayout';
import { Skeleton } from '../components/ui/skeleton';
import { api } from '../services/api';
import {
  CalendarClock, ShieldAlert, ShieldOff, FileText, Wrench,
  TrendingUp, ShoppingBag, ChevronRight, Clock, Sparkles,
} from 'lucide-react';

const TONE = {
  red:    { dot: 'bg-red-400',     ring: 'border-red-500/30',     text: 'text-red-400',     pill: 'bg-red-500/15 text-red-400 border-red-500/25' },
  amber:  { dot: 'bg-amber-400',   ring: 'border-amber-500/30',   text: 'text-amber-400',   pill: 'bg-amber-500/15 text-amber-400 border-amber-500/25' },
  gold:   { dot: 'bg-gold',        ring: 'border-gold/30',        text: 'text-gold',        pill: 'bg-gold/15 text-gold border-gold/25' },
  emerald:{ dot: 'bg-emerald-400', ring: 'border-emerald-500/30', text: 'text-emerald-400', pill: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/25' },
  blue:   { dot: 'bg-[#627EEA]',   ring: 'border-[#627EEA]/30',   text: 'text-[#627EEA]',   pill: 'bg-[#627EEA]/15 text-[#627EEA] border-[#627EEA]/25' },
  violet: { dot: 'bg-violet-400',  ring: 'border-violet-500/30',  text: 'text-violet-400',  pill: 'bg-violet-500/15 text-violet-400 border-violet-500/25' },
  muted:  { dot: 'bg-zinc-500',    ring: 'border-zinc-500/30',    text: 'text-zinc-400',    pill: 'bg-zinc-500/15 text-zinc-400 border-zinc-500/25' },
};

const ICONS = {
  insurance_renewal: CalendarClock,
  insurance_lapsed:  ShieldOff,
  purchase:          ShoppingBag,
  valuation:         TrendingUp,
  maintenance:       Wrench,
  document:          FileText,
};

const TYPE_LABELS = {
  insurance_renewal: 'Insurance',
  insurance_lapsed:  'Cover lapsed',
  purchase:          'Acquisition',
  valuation:         'Revaluation',
  maintenance:       'Maintenance',
  document:          'Document',
};

const FILTER_OPTS = [
  { id: 'all',        label: 'All' },
  { id: 'insurance',  label: 'Insurance' },
  { id: 'maintenance',label: 'Maintenance' },
  { id: 'valuation',  label: 'Valuations' },
  { id: 'purchase',   label: 'Acquisitions' },
];

const formatDate = (iso) => {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
};

const TimelineEvent = ({ ev }) => {
  const tone = TONE[ev.tone] || TONE.muted;
  const Icon = ICONS[ev.type] || Clock;
  const label = TYPE_LABELS[ev.type] || 'Event';

  return (
    <Link
      to={`/assets/${ev.asset_id}`}
      data-testid={`timeline-event-${ev.type}-${ev.asset_id}`}
      className={`relative flex items-start gap-4 p-4 luxury-card hover:border-gold/30 transition-colors group`}
    >
      {/* Date column */}
      <div className="flex flex-col items-center w-16 flex-shrink-0">
        <div className={`w-9 h-9 rounded-full ${tone.pill} border flex items-center justify-center mb-1`}>
          <Icon size={14} />
        </div>
        <p className={`text-[10px] uppercase tracking-wider ${tone.text} font-medium`}>{label}</p>
      </div>

      {/* Body */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2 mb-1">
          <p className="text-white text-sm font-medium truncate group-hover:text-gold transition-colors">
            {ev.title}
          </p>
          <p className="text-[10px] text-muted-foreground whitespace-nowrap">{formatDate(ev.date)}</p>
        </div>
        {ev.detail && (
          <p className="text-xs text-muted-foreground leading-relaxed">{ev.detail}</p>
        )}
      </div>

      <ChevronRight size={14} className="text-muted-foreground self-center flex-shrink-0" />
    </Link>
  );
};

const SectionHeader = ({ title, count, accent }) => (
  <div className="flex items-center gap-2 mb-4 mt-8 first:mt-0">
    <div className={`w-1 h-5 rounded ${accent || 'bg-gold'}`} />
    <h2 className="text-sm uppercase tracking-[0.2em] text-white">{title}</h2>
    {count !== undefined && (
      <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 text-muted-foreground">{count}</span>
    )}
  </div>
);

export default function Timeline() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    api.getTimelineFeed()
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  const matchFilter = (ev) => {
    if (filter === 'all') return true;
    if (filter === 'insurance') return ev.type === 'insurance_renewal' || ev.type === 'insurance_lapsed';
    return ev.type === filter;
  };

  const futureFiltered = useMemo(() => (data?.future || []).filter(matchFilter), [data, filter]);
  const pastFiltered   = useMemo(() => (data?.past   || []).filter(matchFilter), [data, filter]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-6 lg:p-8 max-w-5xl mx-auto" data-testid="timeline-loading">
          <Skeleton className="h-3 w-32 mb-3 bg-gold/10" />
          <Skeleton className="h-9 w-72 mb-8 bg-white/5" />
          <div className="grid grid-cols-3 gap-4 mb-8">
            {[0,1,2].map(i => <Skeleton key={i} className="h-24 bg-white/5" />)}
          </div>
          {[0,1,2,3].map(i => <Skeleton key={i} className="h-20 bg-white/5 mb-3" />)}
        </div>
      </DashboardLayout>
    );
  }

  if (!data) {
    return (
      <DashboardLayout>
        <div className="p-6 lg:p-8 max-w-3xl mx-auto" data-testid="timeline-error">
          <p className="text-muted-foreground">Could not load timeline. Please refresh.</p>
        </div>
      </DashboardLayout>
    );
  }

  const s = data.summary || {};

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8 max-w-5xl mx-auto" data-testid="timeline-page">
        <div className="mb-8">
          <p className="text-gold uppercase tracking-[0.2em] text-xs mb-2">Timeline</p>
          <h1 className="font-serif text-3xl text-white">Every dated event in your vault</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl leading-relaxed">
            Renewals, revaluations, services, and acquisitions - laid out chronologically so nothing slips through the cracks.
          </p>
        </div>

        {/* Summary stats */}
        <div className="grid grid-cols-3 gap-4 mb-8" data-testid="timeline-stats">
          <div className="luxury-card p-4">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Total events</p>
            <p className="font-serif text-2xl text-white">{s.total_events ?? 0}</p>
          </div>
          <div className="luxury-card p-4 border-gold/30">
            <p className="text-[10px] uppercase tracking-wider text-gold mb-1">Upcoming</p>
            <p className="font-serif text-2xl text-gold">{s.upcoming_count ?? 0}</p>
          </div>
          <div className={`luxury-card p-4 ${s.next_30d_count > 0 ? 'border-red-500/30' : ''}`}>
            <p className={`text-[10px] uppercase tracking-wider mb-1 ${s.next_30d_count > 0 ? 'text-red-400' : 'text-muted-foreground'}`}>Next 30 days</p>
            <p className={`font-serif text-2xl ${s.next_30d_count > 0 ? 'text-red-400' : 'text-white'}`}>{s.next_30d_count ?? 0}</p>
          </div>
        </div>

        {/* Filter pills */}
        <div className="flex items-center gap-2 flex-wrap mb-6" data-testid="timeline-filters">
          {FILTER_OPTS.map(f => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              data-testid={`timeline-filter-${f.id}`}
              className={`px-3 py-1.5 text-xs uppercase tracking-wider rounded border transition-colors ${
                filter === f.id
                  ? 'bg-gold/15 text-gold border-gold/40'
                  : 'bg-transparent text-muted-foreground border-[#262626] hover:border-white/20 hover:text-white'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Upcoming */}
        <section data-testid="timeline-upcoming">
          <SectionHeader title="Upcoming" count={futureFiltered.length} accent="bg-gold" />
          {futureFiltered.length === 0 ? (
            <div className="luxury-card p-6 text-center">
              <p className="text-sm text-muted-foreground italic">No upcoming events match this filter.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {futureFiltered.map((ev, i) => <TimelineEvent key={`f-${i}`} ev={ev} />)}
            </div>
          )}
        </section>

        {/* Past */}
        <section data-testid="timeline-past">
          <SectionHeader title="History" count={pastFiltered.length} accent="bg-zinc-500" />
          {pastFiltered.length === 0 ? (
            <div className="luxury-card p-6 text-center">
              <p className="text-sm text-muted-foreground italic">No past events match this filter.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {pastFiltered.slice(0, 50).map((ev, i) => <TimelineEvent key={`p-${i}`} ev={ev} />)}
              {pastFiltered.length > 50 && (
                <p className="text-center text-xs text-muted-foreground pt-2">
                  Showing the most recent 50 of {pastFiltered.length} events
                </p>
              )}
            </div>
          )}
        </section>

        {/* AI hint */}
        <div className="mt-8 luxury-card p-4 flex items-center gap-3 border-gold/20" data-testid="timeline-ai-hint">
          <Sparkles size={16} className="text-gold flex-shrink-0" />
          <p className="text-xs text-muted-foreground flex-1">
            See a renewal you'd like to action? Ask AI Insights to draft the message to your insurer or a reminder for your advisor.
          </p>
          <Link to="/insights">
            <span className="inline-flex items-center gap-1 text-xs text-gold hover:underline whitespace-nowrap">
              Open AI Insights
            </span>
          </Link>
        </div>
      </div>
    </DashboardLayout>
  );
}
